import React, { useState, useCallback, useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import SignatureErrorBoundary from './SignatureErrorBoundary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import '../../styles/richtext.css';

interface Question {
  id: string;
  type: string;
  label?: { [key: string]: string } | string;
  title?: { [key: string]: string } | string;
  required?: boolean; // **ADDED: Direct required field**
  validation?: {
    required?: boolean;
    fileTypes?: string[];
    maxFileSize?: number;
  };
  options?: Array<{ 
    value: string; 
    label: { [key: string]: string } | string;
    isOther?: boolean; // **NEU: Für "Anderes:"-Option**
    articleIds?: string[]; // **NEU: Artikel IDs für diese Option**
  }>;
  infoText?: {
    de: string;
    en: string;
  };
  adminFiles?: Array<{
    originalName: string;
    fileName: string;
    filePath: string;
    size: number;
    mimeType: string;
    uploadedAt: string;
  }>;
  // **NEU: Artikel-spezifische Felder**
  requiresArticleSelection?: boolean;
  allowMultipleArticles?: boolean;
}

interface AttachmentFile {
  originalName: string;
  filePath: string;
  size: number;
  mimeType: string;
}

interface Answer {
  questionId: string;
  answer: any;
  attachments?: (string | AttachmentFile)[];
}

interface QuestionRendererProps {
  question: Question;
  answer?: Answer;
  onSave: (questionId: string, answer: any, files: File[]) => Promise<any>;
  language?: string;
  readOnly?: boolean;
  supplierName?: string; // **NEW: For placeholder replacement**
  supplierId?: string; // **NEW: For supplier-specific article filtering**
  allQuestions?: Question[]; // **NEW: All questions for checking next items**
  currentIndex?: number; // **NEW: Current question index**
  allAnswers?: Map<string, Answer>; // **NEW: All answers map for section sub-questions**
  // **NEU: Artikel-Daten**
  availableArticles?: Array<{
    _id: string;
    articleNumber: string;
    name: string;
    description?: string;
    supplierId: string | { name: string };
  }>;
  selectedArticleAnswer?: string[]; // **NEU: Die vom Benutzer ausgewählten Artikel für diese Frage**
  onArticleSelectionChange?: (articleIds: string[]) => void; // **NEU: Callback für Artikel-Änderungen**
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  answer,
  onSave,
  language = 'de',
  readOnly = false,
  supplierName,
  supplierId,
  allQuestions = [],
  currentIndex = -1,
  allAnswers,
  availableArticles = [],
  selectedArticleAnswer = [],
  onArticleSelectionChange
}) => {
  // **HELPER FUNCTION FOR HTML RENDERING**
  const renderTextContent = (text: string, className: string = "text-gray-700 leading-relaxed") => {
    // Prüfe ob der Text HTML-Tags enthält (von Rich Text Editor)
    const containsHTML = /<\/?(p|strong|em|ul|ol|li|br|div)[^>]*>/i.test(text);
    
    if (containsHTML) {
      return (
        <div 
          className={`${className} rich-text-content`}
          dangerouslySetInnerHTML={{ __html: text }}
          style={{ 
            wordBreak: 'break-word'
          }}
        />
      );
    } else {
      return (
        <div className={`${className} whitespace-pre-wrap`}>
          {text}
        </div>
      );
    }
  };

  // Stelle sicher, dass die Rich Text Styles geladen sind
  useEffect(() => {
    const styleId = 'supplier-rich-text-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        .rich-text-content ul {
          list-style-type: disc !important;
          margin: 0.5rem 0 !important;
          padding-left: 1.5rem !important;
          list-style-position: outside !important;
        }
        
        .rich-text-content ol {
          list-style-type: decimal !important;
          margin: 0.5rem 0 !important;
          padding-left: 1.5rem !important;
          list-style-position: outside !important;
        }
        
        .rich-text-content li {
          display: list-item !important;
          margin-bottom: 0.25rem !important;
        }
        
        .rich-text-content p {
          margin-bottom: 0.5rem !important;
        }
        
        .rich-text-content p:last-child {
          margin-bottom: 0 !important;
        }
        
        .rich-text-content strong {
          font-weight: 600 !important;
        }
        
        .rich-text-content em {
          font-style: italic !important;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);

  // **HELPER FUNCTIONS FOR SAFE LABEL ACCESS**
  const getLabel = (labelObj: { [key: string]: string } | string | undefined, lang: string): string => {
    if (!labelObj) {
      return '';
    }
    if (typeof labelObj === 'string') {
      // **NEW: Replace supplier name placeholder**
      let label = labelObj;
      if (supplierName) {
        label = label.replace(/\{\{SUPPLIER_NAME\}\}/g, supplierName);
      }
      return label;
    }
    let label = labelObj[lang] || labelObj['de'] || labelObj['en'] || Object.values(labelObj)[0] || '';
    // **NEW: Replace supplier name placeholder**
    if (supplierName) {
      label = label.replace(/\{\{SUPPLIER_NAME\}\}/g, supplierName);
    }
    return label;
  };

  const getOptionLabel = (option: { value: string; label: { [key: string]: string } | string }, lang: string): string => {
    const label = getLabel(option.label, lang);
    
    // **FALLBACK: Translate common German values if in English mode**
    if (lang === 'en') {
      const translations: { [key: string]: string } = {
        'Ja': 'Yes',
        'Nein': 'No',
        'ja': 'yes',
        'nein': 'no'
      };
      return translations[label] || label;
    }
    
    return label;
  };

  // **RESET isDirty when question changes**
  useEffect(() => {
    setIsDirty(false);
  }, [question.id]);

  // **NEW: Get following questions (for sections: all questions until next section; for others: only info_text/admin_file)**
  const getFollowingInfoQuestions = (): Question[] => {
    console.log('🔍 getFollowingInfoQuestions Debug:', {
      currentIndex,
      allQuestionsLength: allQuestions?.length,
      currentQuestion: { id: question.id, type: question.type },
      allQuestions: allQuestions?.map(q => ({ id: q.id, type: q.type }))
    });
    
    if (currentIndex < 0 || !allQuestions) {
      console.log('🔍 getFollowingInfoQuestions: currentIndex < 0 oder keine allQuestions');
      return [];
    }
    
    const relatedInfoQuestions: Question[] = [];
    
    // **SPEZIELLE LOGIK FÜR SECTIONS:**
    // Eine Section sammelt ALLE nachfolgenden Fragen bis zur nächsten Section
    if (question.type === 'section') {
      for (let i = currentIndex + 1; i < allQuestions.length; i++) {
        const nextQuestion = allQuestions[i];
        
        // Stoppe wenn wir die nächste Section erreichen
        if (nextQuestion.type === 'section') {
          break;
        }
        
        // Füge alle Fragen hinzu (info_text, admin_file, UND echte Fragen)
        console.log('🔍 Section: Adding following question:', { id: nextQuestion.id, type: nextQuestion.type, index: i });
        relatedInfoQuestions.push(nextQuestion);
      }
    } else {
      // **FÜR NORMALE FRAGEN: Nur info_text und admin_file direkt danach**
      for (let i = currentIndex + 1; i < allQuestions.length; i++) {
        const nextQuestion = allQuestions[i];
        
        // Stoppe wenn wir eine normale Frage oder Section erreichen
        if (nextQuestion.type !== 'info_text' && nextQuestion.type !== 'admin_file') {
          break;
        }
        
        // Füge Info-Text oder Admin-File hinzu
        console.log('🔍 Normal question: Adding following info:', { id: nextQuestion.id, type: nextQuestion.type, index: i });
        relatedInfoQuestions.push(nextQuestion);
      }
    }
    
    console.log('🔍 getFollowingInfoQuestions Result:', {
      currentIndex,
      nextQuestionsChecked: allQuestions.length - currentIndex - 1,
      relatedInfoQuestionsCount: relatedInfoQuestions.length,
      relatedInfoQuestions: relatedInfoQuestions.map(q => ({ id: q.id, type: q.type }))
    });
    
    return relatedInfoQuestions;
  };

  // **STATE MANAGEMENT**
  const [currentAnswer, setCurrentAnswer] = useState<any>('');
  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<(string | AttachmentFile)[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [isValidationError, setIsValidationError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  
  // **SIGNATURE STATE**
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureKey, setSignatureKey] = useState(0); // Force re-render
  const signatureRef = useRef<SignatureCanvas>(null);
  
  // **ARTIKEL SELECTOR STATE**
  const [showArticleSelector, setShowArticleSelector] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState<string | null>(null); // Option-spezifische Modal-Kontrolle
  const [articleSearchQuery, setArticleSearchQuery] = useState(''); // Search query for article selector
  const [articleModalSearchQuery, setArticleModalSearchQuery] = useState(''); // Search query for article modal
  const [optionArticleSelections, setOptionArticleSelections] = useState<{[key: string]: string[]}>({});
  // **OTHER OPTION STATE – component-level to comply with React Rules of Hooks**
  const [otherInputs, setOtherInputs] = useState<{[key: string]: string}>({});

  // Helper functions to close modals and reset search
  const closeArticleSelector = () => {
    setShowArticleSelector(false);
    setArticleSearchQuery('');
  };

  const closeArticleModal = () => {
    setShowArticleModal(null);
    setArticleModalSearchQuery('');
  };

  // **ARTIKEL HELPER FUNCTIONS**
  const getSelectedArticlesForOption = (optionKey: string): string[] => {
    if (!optionKey) return [];
    
    const optionKeyParts = optionKey.split('_');
    const choice = optionKeyParts[optionKeyParts.length - 1]; // 'ja', 'nein', 'text', oder Option-Value
    
    // Für Text-Eingaben: direkt aus articles Array
    if (choice === 'text') {
      if (currentAnswer && typeof currentAnswer === 'object') {
        if (Array.isArray(currentAnswer.articles)) {
          return currentAnswer.articles;
        }
      }
      return optionArticleSelections[optionKey] || [];
    }
    
    // IMMER zuerst aus der echten persistenten Antwort schauen
    if (currentAnswer && typeof currentAnswer === 'object') {
      // Neues Format: {value: 'ja/nein', articles: {ja: [...], nein: [...]}}
      if (currentAnswer.articles && currentAnswer.articles[choice]) {
        return currentAnswer.articles[choice];
      }
      
      // Legacy Format Support: {value: 'ja/nein', selectedArticles: [...]}
      if (currentAnswer.selectedArticles && currentAnswer.value === choice) {
        return currentAnswer.selectedArticles;
      }
    }
    
    // Fallback auf lokalen State (für neue Auswahlen vor dem Speichern)
    return optionArticleSelections[optionKey] || [];
  };

  const handleArticleSelectionForOption = (optionKey: string, selectedArticleIds: string[], closeModal: boolean = false) => {
    if (!optionKey) return;
    
    const optionKeyParts = optionKey.split('_');
    const choice = optionKeyParts[optionKeyParts.length - 1]; // 'ja', 'nein', 'text', oder Option-Value
    const questionId = optionKeyParts.slice(0, -1).join('_'); // Alles bis auf den letzten Teil
    
    console.log('🔧 Debug optionKey parsing:', {
      optionKey,
      optionKeyParts,
      choice,
      questionId
    });
    
    // Update den lokalen State für die Modal-Anzeige
    setOptionArticleSelections(prev => ({
      ...prev,
      [optionKey]: selectedArticleIds
    }));
    
    // Für Text-Eingaben: einfaches Array von Artikel-IDs
    if (choice === 'text') {
      const currentTextValue = typeof currentAnswer === 'object' && currentAnswer?.value ? currentAnswer.value : currentAnswer;
      handleAnswerChange({
        value: currentTextValue,
        articles: selectedArticleIds
      });
      
      if (closeModal || !question.allowMultipleArticles) {
        closeArticleModal();
      }
      return;
    }
    
    // Für Ja/Nein oder Option-basierte Fragen
    let currentValue = currentAnswer;
    let currentChoice = null;
    let allArticleSelections: {[key: string]: string[]} = {};
    
    // Parse aktuelle Antwort
    if (typeof currentValue === 'object' && currentValue?.articles) {
      if (Array.isArray(currentValue.articles)) {
        // Text-Format mit Array
        allArticleSelections[choice] = selectedArticleIds;
        currentChoice = currentValue.value;
      } else {
        // Neues Format: {value: 'ja/nein', articles: {ja: [...], nein: [...]}}
        currentChoice = currentValue.value;
        allArticleSelections = { ...currentValue.articles };
      }
    } else if (typeof currentValue === 'object' && currentValue?.value) {
      // Legacy Format: {value: 'ja/nein', selectedArticles: [...]}
      currentChoice = currentValue.value;
      allArticleSelections = {
        [currentChoice]: currentValue.selectedArticles || []
      };
    } else if (currentValue === true || currentValue === 'ja') {
      currentChoice = 'ja';
      allArticleSelections = {};
    } else if (currentValue === false || currentValue === 'nein') {
      currentChoice = 'nein';
      allArticleSelections = {};
    } else {
      // Für Mehrfach-/Radio-Optionen: bewahre bestehende Wahl
      if (Array.isArray(currentValue)) {
        // Multiselect: Bewahre die aktuelle Array-Auswahl
        currentChoice = currentValue;
      } else {
        // WICHTIG: Für Ja/Nein-Fragen, wenn wir Artikel für eine spezifische Option wählen,
        // müssen wir diese Option als aktuelle Wahl setzen
        if (question.type === 'yesno') {
          // Wenn der User Artikel für 'ja' oder 'nein' wählt, ist das die aktuelle Auswahl
          currentChoice = choice;
        } else if (question.type === 'multiselect') {
          // Für Multiselect: Bewahre die bestehende Auswahl oder verwende ein leeres Array
          currentChoice = currentValue || [];
        } else {
          // Für Radio/Single Select
          currentChoice = currentValue || choice;
        }
      }
      allArticleSelections = {};
    }
    
    // Update die Artikel für diese spezifische Option, bewahre alle anderen
    allArticleSelections[choice] = selectedArticleIds;
    
    // Erstelle neue Antwort basierend auf Fragentyp
    let newAnswer;
    if (question.type === 'multiselect') {
      // Für Multiselect: Bewahre die Array-Struktur, aber füge Artikel als separates Feld hinzu
      newAnswer = {
        value: currentChoice, // Das Array der ausgewählten Optionen
        articles: allArticleSelections
      };
    } else {
      // Für andere Fragentypen (yesno, radio, etc.)
      newAnswer = {
        value: currentChoice, // Die aktuelle Auswahl
        articles: allArticleSelections
      };
    }
    
    console.log('📝 Updating articles:', {
      optionKey,
      choice,
      selectedArticleIds,
      currentChoice,
      allArticleSelections,
      newAnswer
    });
    
    // Setze die neue Antwort (triggert Autosave)
    handleAnswerChange(newAnswer);
    
    // Modal nur schließen wenn explizit angefordert oder bei Einzelauswahl
    if (closeModal || !question.allowMultipleArticles) {
      closeArticleModal();
    }
  };

  const isArticleSelectedInOtherOption = (articleId: string, currentOptionKey: string): boolean => {
    if (!currentOptionKey) return false;
    
    const optionKeyParts = currentOptionKey.split('_');
    const currentChoice = optionKeyParts[optionKeyParts.length - 1]; // 'ja' oder 'nein'
    const questionId = optionKeyParts.slice(0, -1).join('_'); // Alles bis auf den letzten Teil
    
    // Die andere Option bestimmen
    const otherChoice = currentChoice === 'ja' ? 'nein' : 'ja';
    const otherOptionKey = `${questionId}_${otherChoice}`;
    
    // Prüfen ob der Artikel in der anderen Option ausgewählt ist
    const otherOptionArticles = getSelectedArticlesForOption(otherOptionKey);
    const isSelected = otherOptionArticles.includes(articleId);
    
    console.log('🔍 Exclusivity check:', {
      articleId,
      currentOptionKey,
      otherChoice,
      otherOptionKey,
      otherOptionArticles,
      isSelected
    });
    
    return isSelected;
  };

  // **DEBUG LOGGING**
  console.log(`🔄 Question changed to ${question.id}:`, answer);
  console.log(`🔍 Question required status:`, {
    questionId: question.id,
    type: question.type,
    required: question.required,
    validation: question.validation,
    fullQuestion: question
  });

  // **SYNC WITH PARENT ANSWER**
  useEffect(() => {
    console.log(`🔄 Syncing answer for question ${question.id}:`, { 
      hasAnswer: !!answer, 
      isDirty, 
      answer: answer?.answer 
    });
    
    // ALWAYS sync when question changes, regardless of isDirty status
    if (answer) {
      console.log(`✅ Applying saved answer for question ${question.id}:`, answer);
      // **FIX: Don't use || '' for boolean values - false is a valid answer!**
      setCurrentAnswer(answer.answer !== undefined ? answer.answer : '');
      
      // **SYNC ARTIKEL-AUSWAHL mit echter Antwort - WICHTIG: Überschreibt lokalen State**
      if (question.requiresArticleSelection && answer.answer && typeof answer.answer === 'object') {
        const answerObj = answer.answer;
        
        // Komplett den lokalen State zurücksetzen und mit echter Antwort sync
        setOptionArticleSelections({});
        
        if (answerObj.articles) {
          // Neues Format: {value: 'ja/nein', articles: {ja: [...], nein: [...]}}
          const newSelections: {[key: string]: string[]} = {};
          
          Object.entries(answerObj.articles).forEach(([optionValue, articleIds]) => {
            if (optionValue === 'ja' || optionValue === 'nein') {
              const optionKey = `${question.id}_${optionValue}`;
              newSelections[optionKey] = articleIds as string[];
              console.log(`🔄 Syncing articles for ${optionKey}:`, articleIds);
            }
          });
          
          setOptionArticleSelections(newSelections);
          
        } else if (answerObj.value && answerObj.selectedArticles) {
          // Legacy Format: {value: 'ja/nein', selectedArticles: [...]}
          const optionKey = `${question.id}_${answerObj.value}`;
          setOptionArticleSelections({
            [optionKey]: answerObj.selectedArticles
          });
          console.log(`🔄 Syncing legacy articles for ${optionKey}:`, answerObj.selectedArticles);
        }
      } else if (question.requiresArticleSelection) {
        // Wenn keine Artikel-Antwort vorhanden, lokalen State zurücksetzen
        setOptionArticleSelections({});
      }
      
      if (question.type === 'file') {
        const serverAttachments = answer.attachments || [];
        setExistingFiles(serverAttachments);
        console.log(`📁 Initial sync: ${serverAttachments.length} files for question ${question.id}`);
      }
      
      if (question.type === 'signature' && answer.answer) {
        console.log(`✍️ Restoring signature for question ${question.id}:`, answer.answer.substring(0, 50) + '...');
        setCurrentAnswer(answer.answer); // **IMPORTANT: Set currentAnswer first**
        setHasSignature(true);
        
        // **RESTORE SIGNATURE ON CANVAS - WITH DELAY TO ENSURE CANVAS IS READY**
        setTimeout(() => {
          if (signatureRef.current && answer.answer.startsWith('data:image')) {
            try {
              signatureRef.current.fromDataURL(answer.answer);
              console.log(`✅ Signature restored successfully for question ${question.id}`);
            } catch (error) {
              console.error(`❌ Failed to restore signature for question ${question.id}:`, error);
            }
          }
        }, 200); // **INCREASED TIMEOUT**
      }
    } else {
      // No answer yet - reset to defaults
      console.log(`🆕 No saved answer for question ${question.id} - using defaults`);
      setCurrentAnswer('');
      setOptionArticleSelections({});
      if (question.type === 'file') {
        setExistingFiles([]);
      }
      if (question.type === 'signature') {
        setHasSignature(false);
      }
    }
  }, [answer, question.id, question.type]);

  // **ADDITIONAL SIGNATURE RESTORATION - WHEN CANVAS IS READY**
  useEffect(() => {
    if (question.type === 'signature' && hasSignature && currentAnswer && signatureRef.current) {
      // Double-check if signature needs to be restored
      if (signatureRef.current.isEmpty() && currentAnswer.startsWith('data:image')) {
        try {
          signatureRef.current.fromDataURL(currentAnswer);
          console.log(`✍️ Signature re-restored for question ${question.id} after canvas ready`);
        } catch (error) {
          console.error(`❌ Failed to re-restore signature for question ${question.id}:`, error);
        }
      }
    }
  }, [question.type, hasSignature, currentAnswer, signatureKey]); // signatureKey change triggers this

  // **FILE UPLOAD SUCCESS DETECTION**
  useEffect(() => {
    if (answer && question.type === 'file' && !isDirty) {
      const serverAttachments = answer.attachments || [];
      const currentExistingCount = existingFiles.length;
      
      // Only process if server has MORE files than we currently show
      if (serverAttachments.length > currentExistingCount) {
        const newlyUploadedCount = serverAttachments.length - currentExistingCount;
        console.log(`📁 Detected ${newlyUploadedCount} newly uploaded files on server`);
        
        // Update existing files
        setExistingFiles(serverAttachments);
        
        // Remove uploaded files from queue
        if (files.length > 0) {
          setFiles(prev => prev.slice(newlyUploadedCount));
          console.log(`📁 Removed ${newlyUploadedCount} files from upload queue`);
        }
      }
    }
  }, [answer?.attachments, files.length, existingFiles.length, question.type, isDirty]);

  // **SYNC OTHER INPUTS from currentAnswer (for all choice types with isOther option)**
  useEffect(() => {
    if (Array.isArray(currentAnswer)) {
      const newOtherInputs: {[key: string]: string} = {};
      currentAnswer.forEach((value: string) => {
        if (typeof value === 'string' && value.includes(':')) {
          const [optionValue, inputValue] = value.split(':', 2);
          newOtherInputs[optionValue] = inputValue;
        }
      });
      setOtherInputs(newOtherInputs);
    } else if (typeof currentAnswer === 'string' && currentAnswer.includes(':')) {
      const [optionValue, inputValue] = currentAnswer.split(':', 2);
      setOtherInputs({ [optionValue]: inputValue });
    } else {
      setOtherInputs({});
    }
  }, [currentAnswer]);

  // **HELPER FUNCTIONS**
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // **UPDATED: Support both old and new attachment formats**
  const getFileName = useCallback((attachment: string | AttachmentFile): string => {
    // New format with originalName
    if (typeof attachment === 'object' && attachment.originalName) {
      return attachment.originalName;
    }
    // Old format - extract from file path
    const filePath = typeof attachment === 'object' ? attachment.filePath : attachment;
    return filePath.split('\\').pop()?.split('/').pop() || filePath;
  }, []);

  // **NEW: Get original filename for deletion**
  const getOriginalFileName = useCallback((attachment: string | AttachmentFile): string => {
    if (typeof attachment === 'object' && attachment.originalName) {
      return attachment.originalName;
    }
    // Fallback to filename extraction
    const filePath = typeof attachment === 'object' ? attachment.filePath : attachment;
    return filePath.split('\\').pop()?.split('/').pop() || filePath;
  }, []);

  // **ANSWER VALIDATION**
  const validateAnswer = useCallback((value: any): boolean => {
    const isRequired = question.required || question.validation?.required;
    if (isRequired) {
      if (question.type === 'multiselect') {
        return Array.isArray(value) && value.length > 0;
      }
      if (question.type === 'file') {
        return existingFiles.length > 0 || files.length > 0;
      }
      if (question.type === 'signature') {
        return hasSignature;
      }
      return value !== '' && value !== null && value !== undefined;
    }
    return true;
  }, [question.required, question.validation?.required, question.type, existingFiles.length, files.length, hasSignature]);

  // **CHECK IF REQUIRED FIELD IS EMPTY**
  const isRequiredAndEmpty = useCallback((): boolean => {
    const isRequired = question.required || question.validation?.required;
    if (!isRequired) return false;
    
    if (question.type === 'multiselect') {
      return !Array.isArray(currentAnswer) || currentAnswer.length === 0;
    }
    if (question.type === 'file') {
      return existingFiles.length === 0 && files.length === 0;
    }
    if (question.type === 'signature') {
      return !hasSignature && (!currentAnswer || !currentAnswer.startsWith('data:image/'));
    }
    if (question.type === 'checkbox' && (!question.options || question.options.length === 0)) {
      // Single checkbox (yes/no) - false is a valid answer
      return currentAnswer === null || currentAnswer === undefined;
    }
    if (question.type === 'checkbox' && question.options && question.options.length > 0) {
      // Multiple checkboxes - need at least one selected
      return !Array.isArray(currentAnswer) || currentAnswer.length === 0;
    }
    
    return !currentAnswer || currentAnswer === '' || currentAnswer === '""';
  }, [question.required, question.validation?.required, question.type, currentAnswer, existingFiles.length, files.length, hasSignature, question.options]);

  // **GET INPUT CLASSES WITH REQUIRED STYLING**
  const getInputClasses = useCallback((baseClasses: string): string => {
    const requiredClasses = isRequiredAndEmpty() 
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';
    
    return `${baseClasses} ${requiredClasses}`;
  }, [isRequiredAndEmpty]);

  // **SAVE FUNCTION**
  const handleSave = useCallback(async () => {
    if (!isDirty || isSaving) return;
    
    const trimmedAnswer = typeof currentAnswer === 'string' ? currentAnswer.trim() : currentAnswer;
    
    // **DEBUG: Log save details especially for signatures**
    if (question.type === 'signature') {
      console.log('✍️ SIGNATURE HANDLESAVE DEBUG:', {
        questionId: question.id,
        questionType: question.type,
        currentAnswer: trimmedAnswer,
        answerType: typeof trimmedAnswer,
        answerLength: trimmedAnswer ? trimmedAnswer.length : 0,
        isBase64: trimmedAnswer ? trimmedAnswer.startsWith('data:image') : false,
        isDirty: isDirty,
        isSaving: isSaving,
        filesLength: files.length
      });
    }

    // **VALIDATION - REQUIRED FIELDS**
    if (question.validation?.required) {
      if (question.type === 'multiselect') {
        if (!Array.isArray(currentAnswer) || currentAnswer.length === 0) {
          setValidationMessage(language === 'en' ? 'Please select at least one option' : 'Bitte wählen Sie mindestens eine Option');
          setIsValidationError(true);
          return;
        }
      }
      if (question.type === 'file') {
        if (existingFiles.length === 0 && files.length === 0) {
          setValidationMessage(language === 'en' ? 'Please upload a file' : 'Bitte laden Sie eine Datei hoch');
          setIsValidationError(true);
          return;
        }
      }
      if (question.type === 'signature') {
        if (!hasSignature) {
          setValidationMessage(language === 'en' ? 'Please draw a signature' : 'Bitte zeichnen Sie eine Unterschrift');
          setIsValidationError(true);
          return;
        }
      }
      if (currentAnswer === '' || currentAnswer === null || currentAnswer === undefined) {
        setValidationMessage(language === 'en' ? 'This field is required' : 'Dieses Feld ist erforderlich');
        setIsValidationError(true);
        return;
      }
    }
    
    setIsSaving(true);
    setValidationMessage('');
    setIsValidationError(false);
    
    try {
      let finalAnswer = currentAnswer;
      
      // Handle signature
      if (question.type === 'signature' && signatureRef.current && hasSignature) {
        // Only get signature if canvas is not empty
        if (!signatureRef.current.isEmpty()) {
          finalAnswer = signatureRef.current.toDataURL();
          console.log(`✍️ Saving signature as DataURL for question ${question.id}:`, finalAnswer.substring(0, 50) + '...');
        } else {
          finalAnswer = '';
          console.log(`✍️ Canvas is empty, saving empty signature for question ${question.id}`);
        }
      }
      
      const result = await onSave(question.id, finalAnswer, files);
      
      console.log(`✅ Save completed for ${question.id}:`, result);
      setIsDirty(false);
      
    } catch (error) {
      console.error(`❌ Save failed for ${question.id}:`, error);
      setValidationMessage(language === 'en' ? 'Error saving' : 'Fehler beim Speichern');
      setIsValidationError(true);
    } finally {
      setIsSaving(false);
    }
  }, [currentAnswer, files, question.id, question.type, validateAnswer, onSave, isSaving, hasSignature, isDirty]);

  // **AUTO-SAVE TRIGGER - AUSGESCHLOSSEN FÜR FILE UPLOADS**
  useEffect(() => {
    // **SKIP AUTO-SAVE FOR FILE UPLOADS - THEY UPLOAD IMMEDIATELY**
    if (question.type === 'file') {
      return;
    }
    
    if (isDirty && !isSaving) {
      const timer = setTimeout(() => {
        handleSave();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isDirty, handleSave, isSaving, question.type]);

  // **ANSWER CHANGE HANDLER**
  const handleAnswerChange = useCallback((value: any) => {
    console.log(`📝 Answer changing for ${question.id}:`, value);
    setCurrentAnswer(value);
    setIsDirty(true);
    setValidationMessage('');
    setIsValidationError(false);
  }, [question.id]);

  // **FILE HANDLING - IMMEDIATE UPLOAD**
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    console.log(`📁 Files selected:`, selectedFiles.map(f => f.name));
    
    if (selectedFiles.length === 0) return;

    // **FALLBACK FILE TYPES - Falls question.validation.fileTypes leer ist**
    const allowedTypes = (question.validation?.fileTypes && question.validation.fileTypes.length > 0) 
      ? question.validation.fileTypes 
      : ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
      
    console.log(`📋 Allowed types:`, allowedTypes);
    
    const maxSize = question.validation?.maxFileSize || (10 * 1024 * 1024); // 10MB default
    
    const validFiles: File[] = [];
    const rejectedFiles: { file: File; reason: string }[] = [];
    
    selectedFiles.forEach(file => {
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      
      if (file.size > maxSize) {
        rejectedFiles.push({ 
          file, 
          reason: `Datei zu groß (max. ${formatFileSize(maxSize)})` 
        });
      } else if (!allowedTypes.includes(fileExt)) {
        rejectedFiles.push({ 
          file, 
          reason: `Dateiformat nicht erlaubt (.${fileExt})` 
        });
      } else {
        validFiles.push(file);
      }
    });
    
    if (rejectedFiles.length > 0) {
      console.log(`❌ Rejected files:`, rejectedFiles);
      setValidationMessage(
        language === 'en'
          ? `${rejectedFiles.length} file(s) rejected: ${rejectedFiles.map(r => r.reason).join(', ')}`
          : `${rejectedFiles.length} Datei(en) abgelehnt: ${rejectedFiles.map(r => r.reason).join(', ')}`
      );
      setIsValidationError(true);
    } else {
      setValidationMessage('');
      setIsValidationError(false);
    }
    
    if (validFiles.length > 0) {
      console.log(`✅ Adding ${validFiles.length} valid files`);
      setFiles(prev => [...prev, ...validFiles]);
      
      // **IMMEDIATE UPLOAD FOR FILES - NO AUTO-SAVE DELAY**
      console.log('📁 Triggering immediate file upload...');
      setIsDirty(true); // Set dirty to trigger UI updates
      
      // **IMMEDIATE SAVE WITHOUT DELAY**
      setTimeout(async () => {
        try {
          setIsSaving(true);
          setValidationMessage('');
          setIsValidationError(false);
          
          const result = await onSave(question.id, '', validFiles); // Empty answer, just files
          
          console.log(`✅ File upload completed:`, result);
          setIsDirty(false);
          
        } catch (error) {
          console.error(`❌ File upload failed:`, error);
          setValidationMessage(language === 'en' ? 'Error uploading files' : 'Fehler beim Hochladen der Dateien');
          setIsValidationError(true);
        } finally {
          setIsSaving(false);
        }
      }, 50); // Minimal delay just for UI update
    }
    
    e.target.value = '';
  }, [question.validation?.maxFileSize, question.validation?.fileTypes, formatFileSize, onSave]);

  // **DRAG & DROP - IMMEDIATE UPLOAD**
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    console.log(`📁 Files dropped:`, droppedFiles.map(f => f.name));
    
    // **FALLBACK FILE TYPES**
    const allowedTypes = (question.validation?.fileTypes && question.validation.fileTypes.length > 0) 
      ? question.validation.fileTypes 
      : ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
  
    const maxSize = question.validation?.maxFileSize || (10 * 1024 * 1024);
    
    const validFiles: File[] = [];
    const rejectedFiles: { file: File; reason: string }[] = [];
    
    droppedFiles.forEach(file => {
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      
      if (file.size > maxSize) {
        rejectedFiles.push({ 
          file, 
          reason: language === 'en' 
            ? `File too large (max. ${formatFileSize(maxSize)})`
            : `Datei zu groß (max. ${formatFileSize(maxSize)})` 
        });
      } else if (!allowedTypes.includes(fileExt)) {
        rejectedFiles.push({ 
          file, 
          reason: language === 'en'
            ? `File format not allowed (.${fileExt})`
            : `Dateiformat nicht erlaubt (.${fileExt})` 
        });
      } else {
        validFiles.push(file);
      }
    });
    
    if (rejectedFiles.length > 0) {
      console.log(`❌ Rejected dropped files:`, rejectedFiles);
      setValidationMessage(
        language === 'en'
          ? `${rejectedFiles.length} file(s) rejected: ${rejectedFiles.map(r => r.reason).join(', ')}`
          : `${rejectedFiles.length} Datei(en) abgelehnt: ${rejectedFiles.map(r => r.reason).join(', ')}`
      );
      setIsValidationError(true);
    } else {
      setValidationMessage('');
      setIsValidationError(false);
    }
    
    if (validFiles.length > 0) {
      console.log(`✅ Adding ${validFiles.length} valid dropped files`);
      setFiles(prev => [...prev, ...validFiles]);
      
      // **IMMEDIATE UPLOAD FOR DROPPED FILES**
      console.log('📁 Triggering immediate drop upload...');
      setIsDirty(true);
      
      // **IMMEDIATE SAVE WITHOUT DELAY**
      setTimeout(async () => {
        try {
          setIsSaving(true);
          setValidationMessage('');
          setIsValidationError(false);
          
          const result = await onSave(question.id, '', validFiles); // Empty answer, just files
          
          console.log(`✅ Drop upload completed:`, result);
          setIsDirty(false);
          
        } catch (error) {
          console.error(`❌ Drop upload failed:`, error);
          setValidationMessage(language === 'en' ? 'Error uploading files' : 'Fehler beim Hochladen der Dateien');
          setIsValidationError(true);
        } finally {
          setIsSaving(false);
        }
      }, 50);
    }
  }, [question.validation?.fileTypes, question.validation?.maxFileSize, formatFileSize, onSave]);

  // **FILE DELETION - UPDATED**
  const removeExistingFile = useCallback(async (index: number) => {
    const attachmentToDelete = existingFiles[index];
    const filename = getOriginalFileName(attachmentToDelete);
    
    console.log(`🗑️ Attempting to delete file: ${filename}`, attachmentToDelete);
    
    try {
      // **DETECT PORTAL MODE**
      const currentUrl = window.location.pathname;
      const portalMatch = currentUrl.match(/\/supplier\/portal\/([^\/]+)\/questionnaire\/([^\/]+)/);
      const isPortalMode = !!portalMatch;
      
      if (isPortalMode) {
        const [, masterToken, accessToken] = portalMatch;
        console.log(`🗑️ Portal mode detected - Master: ${masterToken}, Access: ${accessToken}`);
        
        // **GET SESSION TOKEN**
        const sessionToken = sessionStorage.getItem('supplierPortalSession');
        if (!sessionToken) {
          throw new Error('Session expired');
        }
        
        // **OPTIMISTIC UPDATE - REMOVE FROM UI IMMEDIATELY**
        const newExistingFiles = existingFiles.filter((_, i) => i !== index);
        setExistingFiles(newExistingFiles);
        
        // **CORRECTED API URL - USE FULL BASE URL**
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const fullApiUrl = `${apiUrl}/public/portal/${masterToken}/questionnaire/${accessToken}/answer/${question.id}/attachment`;
        console.log(`🗑️ Using full API URL: ${fullApiUrl}`);
        
        const response = await fetch(fullApiUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: filename,
            sessionToken: sessionToken
          })
        });

        if (!response.ok) {
          // **REVERT OPTIMISTIC UPDATE ON ERROR**
          setExistingFiles(existingFiles);
          const errorData = await response.json();
          throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
        }

        const result = await response.json();
        
        // **UPDATE WITH SERVER RESPONSE**
        if (result.remainingAttachments) {
          setExistingFiles(result.remainingAttachments);
        }
        
        console.log(`✅ File deleted successfully:`, result);
        setValidationMessage(language === 'en' ? 'File successfully deleted' : 'Datei erfolgreich gelöscht');
        setIsValidationError(true); // **CHANGED TO TRUE FOR RED COLOR**
        
        // **TRIGGER PARENT UPDATE**
        if (onSave) {
          // Call save to update parent component with new file list
          await onSave(question.id, currentAnswer, []);
        }
        
      } else {
        // **LEGACY MODE - OLD API**
        const sessionToken = sessionStorage.getItem('supplierSessionToken');
        if (!sessionToken) {
          throw new Error('Session expired');
        }
        
        const currentUrl = window.location.pathname;
        const accessToken = currentUrl.split('/').pop();
        
        // **OPTIMISTIC UPDATE - REMOVE FROM UI IMMEDIATELY**
        const newExistingFiles = existingFiles.filter((_, i) => i !== index);
        setExistingFiles(newExistingFiles);
        
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const fullApiUrl = `${apiUrl}/public/questionnaire/${accessToken}/answer/${question.id}/attachment`;
        
        const response = await fetch(fullApiUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: filename,
            sessionToken: sessionToken
          })
        });

        if (!response.ok) {
          // **REVERT OPTIMISTIC UPDATE ON ERROR**
          setExistingFiles(existingFiles);
          const errorData = await response.json();
          throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
        }

        const result = await response.json();
        
        // **UPDATE WITH SERVER RESPONSE**
        if (result.remainingAttachments) {
          setExistingFiles(result.remainingAttachments);
        }
        
        console.log(`✅ File deleted successfully:`, result);
        setValidationMessage(language === 'en' ? 'File successfully deleted' : 'Datei erfolgreich gelöscht');
        setIsValidationError(true); // **CHANGED TO TRUE FOR RED COLOR**
        
        // **TRIGGER PARENT UPDATE**
        if (onSave) {
          await onSave(question.id, currentAnswer, []);
        }
      }
      
    } catch (error) {
      console.error('❌ Error deleting file:', error);
      setValidationMessage(
        language === 'en' 
          ? 'Error deleting file: ' + (error as Error).message
          : 'Fehler beim Löschen der Datei: ' + (error as Error).message
      );
      setIsValidationError(true);
    }
  }, [existingFiles, question.id, getOriginalFileName, currentAnswer, onSave]);

  const removeQueuedFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    console.log(`🗑️ Removed queued file at index ${index}`);
  }, []);

  // **SIGNATURE HANDLERS**
  const handleSignatureConfirm = useCallback(() => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureDataURL = signatureRef.current.toDataURL();
      console.log('✍️ SIGNATURE SAVE DEBUG:', {
        questionId: question.id,
        signatureLength: signatureDataURL.length,
        signatureStart: signatureDataURL.substring(0, 50),
        isBase64: signatureDataURL.startsWith('data:image'),
        canvasIsEmpty: signatureRef.current.isEmpty()
      });
      
      setCurrentAnswer(signatureDataURL);
      setHasSignature(true);
      setIsDirty(true);
      setValidationMessage('');
      setIsValidationError(false);
      
      // **AUTO-SAVE SIGNATURE**
      setTimeout(() => {
        handleSave();
      }, 200);
    } else {
      setValidationMessage(language === 'en' ? 'Please draw a signature' : 'Bitte zeichnen Sie eine Unterschrift');
      setIsValidationError(true);
    }
  }, [handleSave, question.id]);

  const handleSignatureClear = useCallback(() => {
    console.log('🗑️ handleSignatureClear called:', {
      hasSignature,
      currentAnswer,
      signatureRefExists: !!signatureRef.current
    });
    
    // **CLEAR SIGNATURE STATE REGARDLESS OF CANVAS STATE**
    setHasSignature(false);
    setCurrentAnswer('');
    setIsDirty(true);
    
    // **CLEAR CANVAS IF IT EXISTS**
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    
    // Force re-render to reset canvas
    setSignatureKey(prev => prev + 1);
    
    console.log('🗑️ Signature cleared, auto-saving...');
    
    // **AUTO-SAVE EMPTY SIGNATURE**
    setTimeout(() => {
      handleSave();
    }, 200);
  }, [handleSave, hasSignature, currentAnswer]);

  // **RENDER METHODS**
  const renderTextInput = () => (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={typeof currentAnswer === 'object' && currentAnswer?.value ? currentAnswer.value : currentAnswer}
          onChange={(e) => {
            if (question.requiresArticleSelection) {
              // Bewahre die Artikel-Auswahl
              const currentArticles = typeof currentAnswer === 'object' && currentAnswer?.articles ? currentAnswer.articles : [];
              handleAnswerChange({
                value: e.target.value,
                articles: currentArticles
              });
            } else {
              handleAnswerChange(e.target.value);
            }
          }}
          className={getInputClasses("flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2")}
          placeholder={language === 'en' 
            ? ((question.required || question.validation?.required) ? "Required field - Please fill in" : "Your answer...") 
            : ((question.required || question.validation?.required) ? "Pflichtfeld - Bitte ausfüllen" : "Ihre Antwort...")
          }
          disabled={readOnly}
        />
        {/* Artikel-Auswahl Button */}
        {question.requiresArticleSelection && (
          <button
            type="button"
            onClick={() => setShowArticleModal(`${question.id}_text`)}
            className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center"
          >
            <FontAwesomeIcon icon={faBox} className="mr-1" />
            {(() => {
              const currentArticles = typeof currentAnswer === 'object' && currentAnswer?.articles ? currentAnswer.articles : [];
              const count = Array.isArray(currentArticles) ? currentArticles.length : 0;
              const articleText = language === 'en' ? 'Article' : 'Artikel';
              const selectText = language === 'en' ? 'Select articles' : 'Artikel wählen';
              return count > 0 ? `${count} ${articleText}${count !== 1 ? (language === 'en' ? 's' : '') : ''}` : selectText;
            })()}
          </button>
        )}
      </div>
    </div>
  );

  const renderTextarea = () => (
    <div className="space-y-2">
      <textarea
        value={typeof currentAnswer === 'object' && currentAnswer?.value ? currentAnswer.value : currentAnswer}
        onChange={(e) => {
          if (question.requiresArticleSelection) {
            // Bewahre die Artikel-Auswahl
            const currentArticles = typeof currentAnswer === 'object' && currentAnswer?.articles ? currentAnswer.articles : [];
            handleAnswerChange({
              value: e.target.value,
              articles: currentArticles
            });
          } else {
            handleAnswerChange(e.target.value);
          }
        }}
        rows={4}
        className={getInputClasses("w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2")}
        placeholder={language === 'en' 
          ? ((question.required || question.validation?.required) ? "Required field - Please fill in" : "Your answer...") 
          : ((question.required || question.validation?.required) ? "Pflichtfeld - Bitte ausfüllen" : "Ihre Antwort...")
        }
        disabled={readOnly}
      />
      {/* Artikel-Auswahl Button für Textarea */}
      {question.requiresArticleSelection && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowArticleModal(`${question.id}_text`)}
            className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center"
          >
            <FontAwesomeIcon icon={faBox} className="mr-1" />
            {(() => {
              const currentArticles = typeof currentAnswer === 'object' && currentAnswer?.articles ? currentAnswer.articles : [];
              const count = Array.isArray(currentArticles) ? currentArticles.length : 0;
              const articleText = language === 'en' ? (count === 1 ? 'Article' : 'Articles') : 'Artikel';
              const selectText = language === 'en' ? 'Select articles' : 'Artikel wählen';
              return count > 0 ? `${count} ${articleText}` : selectText;
            })()}
          </button>
        </div>
      )}
    </div>
  );

  const renderSelect = () => {
    const selectedOtherOption = question.options?.find(option => option.isOther && (currentAnswer === option.value || currentAnswer?.startsWith(`${option.value}:`)));
    const otherInput = otherInputs[selectedOtherOption?.value || ''] || '';

    const handleSelectChange = (value: string) => {
      const option = question.options?.find(opt => opt.value === value);
      const existingText = otherInputs[value] || '';
      if (option?.isOther && existingText) {
        // Wenn "Anderes" Option gewählt wird und bereits Text vorhanden ist, kombiniere
        const combinedValue = `${value}:${existingText}`;
        handleAnswerChange(combinedValue);
      } else {
        handleAnswerChange(value);
      }
    };

    const handleOtherInputChange = (inputValue: string) => {
      if (selectedOtherOption) {
        setOtherInputs(prev => ({ ...prev, [selectedOtherOption.value]: inputValue }));
        const combinedValue = `${selectedOtherOption.value}:${inputValue}`;
        handleAnswerChange(combinedValue);
      }
    };

    return (
      <div className="space-y-2">
        <select
          value={selectedOtherOption ? selectedOtherOption.value : currentAnswer}
          onChange={(e) => handleSelectChange(e.target.value)}
          className={getInputClasses("w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2")}
          disabled={readOnly}
        >
          <option value="">{language === 'en' 
            ? ((question.required || question.validation?.required) ? "Required field - Please select..." : "Please select...") 
            : ((question.required || question.validation?.required) ? "Pflichtfeld - Bitte auswählen..." : "Bitte auswählen...")
          }</option>
          {question.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {getOptionLabel(option, language)}
            </option>
          ))}
        </select>
        {/* Textfeld für "Anderes:" wenn entsprechende Option gewählt ist */}
        {selectedOtherOption && (
          <input
            type="text"
            placeholder={language === 'en' ? 'Please specify...' : 'Bitte spezifizieren...'}
            value={otherInput}
            onChange={(e) => handleOtherInputChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={readOnly}
          />
        )}
      </div>
    );
  };

  // **YES/NO RADIO INPUT**
  const renderYesNo = () => {
    const isYes = currentAnswer === true || currentAnswer === 'yes' || currentAnswer === 'ja';
    const isNo = currentAnswer === false || currentAnswer === 'no' || currentAnswer === 'nein';
    
    // Translate Yes/No based on language
    const yesLabel = language === 'en' ? 'Yes' : 'Ja';
    const noLabel = language === 'en' ? 'No' : 'Nein';
    
    return (
      <div className="space-y-3">
        <div className="flex space-x-6">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name={`yesno-${question.id}`}
              value="true"
              checked={isYes}
              onChange={() => handleAnswerChange(true)}
              className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
              disabled={readOnly}
            />
            <span className="text-gray-900 font-medium text-green-700">{yesLabel}</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name={`yesno-${question.id}`}
              value="false"
              checked={isNo}
              onChange={() => handleAnswerChange(false)}
              className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
              disabled={readOnly}
            />
            <span className="text-gray-900 font-medium text-red-700">{noLabel}</span>
          </label>
        </div>
      </div>
    );
  };

  // **SECTION HEADER**
  const renderSection = () => {
    return (
      <div className="my-8 first:mt-0">
        <h2 className="text-xl font-bold text-gray-900 border-b-2 border-blue-500 pb-3 mb-6">
          {getLabel(question.label || question.title, language)}
        </h2>
      </div>
    );
  };

  // **RADIO BUTTONS**
  const renderRadio = () => {

    const handleRadioChange = (optionValue: string) => {
      // Wenn eine "Anderes" Option gewählt wird, behalte den vorhandenen Text bei
      const option = question.options?.find(opt => opt.value === optionValue);
      if (option?.isOther && otherInputs[optionValue]) {
        const combinedValue = `${optionValue}:${otherInputs[optionValue]}`;
        handleAnswerChange(combinedValue);
      } else {
        handleAnswerChange(optionValue);
      }
    };

    const handleOtherInputChange = (optionValue: string, inputValue: string) => {
      setOtherInputs(prev => ({
        ...prev,
        [optionValue]: inputValue
      }));
      
      // Kombiniere die Option mit dem eingegebenen Text
      const combinedValue = `${optionValue}:${inputValue}`;
      handleAnswerChange(combinedValue);
    };

    return (
      <div className="space-y-3">
        {question.options?.map((option) => {
          const isSelected = currentAnswer === option.value || (option.isOther && currentAnswer?.startsWith(`${option.value}:`));
          const otherInputValue = otherInputs[option.value] || '';
          
          return (
            <div key={option.value}>
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name={`radio-${question.id}`}
                    value={option.value}
                    checked={isSelected}
                    onChange={(e) => handleRadioChange(e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    disabled={readOnly}
                  />
                  <span className="text-gray-900">{getOptionLabel(option, language)}</span>
                </label>
                {/* Artikel-Auswahl Button für diese Option */}
                {question.requiresArticleSelection && (
                  <button
                    type="button"
                    onClick={() => setShowArticleModal(`${question.id}_${option.value}`)}
                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faBox} className="mr-1" />
                    {getSelectedArticlesForOption(`${question.id}_${option.value}`).length > 0
                      ? `${getSelectedArticlesForOption(`${question.id}_${option.value}`).length} ${language === 'en' ? (getSelectedArticlesForOption(`${question.id}_${option.value}`).length === 1 ? 'Article' : 'Articles') : 'Artikel'}`
                      : (language === 'en' ? 'Select articles' : 'Artikel wählen')
                    }
                  </button>
                )}
              </div>
              {/* Textfeld für "Anderes:" Option */}
              {option.isOther && isSelected && (
                <div className="ml-7 mt-2">
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Please specify...' : 'Bitte spezifizieren...'}
                    value={otherInputValue}
                    onChange={(e) => handleOtherInputChange(option.value, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // **CHECKBOX INPUT (for Yes/No questions)**
  const renderCheckbox = () => {

    // Check if this is a simple yes/no checkbox or has options
    if (question.options && question.options.length > 0) {
      // Multiple checkbox options
      const selectedValues = Array.isArray(currentAnswer) ? currentAnswer : [];
      
      const handleOptionChange = (optionValue: string, checked: boolean) => {
        let newValues;
        if (checked) {
          const option = question.options?.find(opt => opt.value === optionValue);
          if (option?.isOther && otherInputs[optionValue]) {
            // Wenn "Anderes" Option gewählt wird und bereits Text vorhanden ist, kombiniere
            const combinedValue = `${optionValue}:${otherInputs[optionValue]}`;
            newValues = [...selectedValues, combinedValue];
          } else {
            newValues = [...selectedValues, optionValue];
          }
        } else {
          newValues = selectedValues.filter(v => v !== optionValue && !v.startsWith(`${optionValue}:`));
        }
        handleAnswerChange(newValues);
      };

      const handleOtherInputChange = (optionValue: string, inputValue: string) => {
        setOtherInputs(prev => ({
          ...prev,
          [optionValue]: inputValue
        }));
        
        // Ersetze die bestehende Option mit der kombinierten Version (entferne sowohl plain als auch combined)
        const currentValues = Array.isArray(currentAnswer) ? currentAnswer : [];
        const newValues = currentValues.filter(v => v !== optionValue && !v.startsWith(`${optionValue}:`));
        const combinedValue = `${optionValue}:${inputValue}`;
        newValues.push(combinedValue);
        handleAnswerChange(newValues);
      };

      return (
        <div className="space-y-3">
          {question.options.map((option) => {
            const isSelected = selectedValues.includes(option.value) || selectedValues.some(v => v.startsWith(`${option.value}:`));
            const otherInputValue = otherInputs[option.value] || '';
            
            return (
              <div key={option.value}>
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={isSelected}
                      onChange={(e) => handleOptionChange(option.value, e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      disabled={readOnly}
                    />
                    <span className="text-gray-900">{getOptionLabel(option, language)}</span>
                  </label>
                  {/* Artikel-Auswahl Button für diese Option */}
                  {question.requiresArticleSelection && (
                    <button
                      type="button"
                      onClick={() => setShowArticleModal(`${question.id}_${option.value}`)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                    >
                      <FontAwesomeIcon icon={faBox} className="mr-1" />
                      {getSelectedArticlesForOption(`${question.id}_${option.value}`).length > 0
                        ? `${getSelectedArticlesForOption(`${question.id}_${option.value}`).length} ${language === 'en' ? (getSelectedArticlesForOption(`${question.id}_${option.value}`).length === 1 ? 'Article' : 'Articles') : 'Artikel'}`
                        : (language === 'en' ? 'Select articles' : 'Artikel wählen')
                      }
                    </button>
                  )}
                </div>
                {/* Textfeld für "Anderes:" Option */}
                {option.isOther && isSelected && (
                  <div className="ml-7 mt-2">
                    <input
                      type="text"
                      placeholder={language === 'en' ? 'Please specify...' : 'Bitte spezifizieren...'}
                      value={otherInputValue}
                      onChange={(e) => handleOtherInputChange(option.value, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={readOnly}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    } else {
      // Simple yes/no checkbox - zeige beide Optionen
      const isYes = currentAnswer === true || currentAnswer === 'yes' || currentAnswer === 'ja' ||
                    (typeof currentAnswer === 'object' && currentAnswer?.value === 'ja');
      const isNo = currentAnswer === false || currentAnswer === 'no' || currentAnswer === 'nein' ||
                   (typeof currentAnswer === 'object' && currentAnswer?.value === 'nein');
      
      // Translate Yes/No based on language
      const yesLabel = language === 'en' ? 'Yes' : 'Ja';
      const noLabel = language === 'en' ? 'No' : 'Nein';
      
      return (
        <div className="space-y-3">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name={`checkbox-yesno-${question.id}`}
                  value="true"
                  checked={isYes}
                  onChange={() => {
                    if (question.requiresArticleSelection) {
                      // Hole alle bereits ausgewählten Artikel beider Optionen
                      const jaArticles = getSelectedArticlesForOption(`${question.id}_ja`);
                      const neinArticles = getSelectedArticlesForOption(`${question.id}_nein`);
                      
                      handleAnswerChange({
                        value: 'ja',
                        articles: {
                          ja: jaArticles,
                          nein: neinArticles
                        }
                      });
                    } else {
                      handleAnswerChange(true);
                    }
                  }}
                  className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                  disabled={readOnly}
                />
                <span className="text-gray-900 font-medium text-green-700">{yesLabel}</span>
              </label>
              {/* Artikel-Auswahl Button für JA */}
              {question.requiresArticleSelection && (
                <button
                  type="button"
                  onClick={() => setShowArticleModal(`${question.id}_ja`)}
                  className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                >
                  <FontAwesomeIcon icon={faBox} className="mr-1" />
                  {getSelectedArticlesForOption(`${question.id}_ja`).length > 0
                    ? `${getSelectedArticlesForOption(`${question.id}_ja`).length} ${language === 'en' ? (getSelectedArticlesForOption(`${question.id}_ja`).length === 1 ? 'Article' : 'Articles') : 'Artikel'}`
                    : (language === 'en' ? 'Select articles' : 'Artikel wählen')
                  }
                </button>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name={`checkbox-yesno-${question.id}`}
                  value="false"
                  checked={isNo}
                  onChange={() => {
                    if (question.requiresArticleSelection) {
                      // Hole alle bereits ausgewählten Artikel beider Optionen
                      const jaArticles = getSelectedArticlesForOption(`${question.id}_ja`);
                      const neinArticles = getSelectedArticlesForOption(`${question.id}_nein`);
                      
                      handleAnswerChange({
                        value: 'nein',
                        articles: {
                          ja: jaArticles,
                          nein: neinArticles
                        }
                      });
                    } else {
                      handleAnswerChange(false);
                    }
                  }}
                  className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                  disabled={readOnly}
                />
                <span className="text-gray-900 font-medium text-red-700">{noLabel}</span>
              </label>
              {/* Artikel-Auswahl Button für NEIN */}
              {question.requiresArticleSelection && (
                <button
                  type="button"
                  onClick={() => setShowArticleModal(`${question.id}_nein`)}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                >
                  <FontAwesomeIcon icon={faBox} className="mr-1" />
                  {getSelectedArticlesForOption(`${question.id}_nein`).length > 0
                    ? `${getSelectedArticlesForOption(`${question.id}_nein`).length} ${language === 'en' ? (getSelectedArticlesForOption(`${question.id}_nein`).length === 1 ? 'Article' : 'Articles') : 'Artikel'}`
                    : (language === 'en' ? 'Select articles' : 'Artikel wählen')
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  // **DATE INPUT**
  const renderDate = () => (
    <div className="space-y-2">
      <input
        type="date"
        value={currentAnswer}
        onChange={(e) => handleAnswerChange(e.target.value)}
        className={getInputClasses("w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2")}
        disabled={readOnly}
      />
      {(question.required || question.validation?.required) && isRequiredAndEmpty() && (
        <p className="text-sm text-red-600">
          ⚠️ Bitte wählen Sie ein Datum aus
        </p>
      )}
    </div>
  );

  const renderMultiselect = () => {
    // Behandle sowohl Array-Format als auch Objekt-Format mit Artikeln
    const selectedValues = Array.isArray(currentAnswer) 
      ? currentAnswer 
      : (typeof currentAnswer === 'object' && currentAnswer?.value && Array.isArray(currentAnswer.value))
        ? currentAnswer.value
        : [];
    
    const handleOptionChange = (optionValue: string, checked: boolean) => {
      let newValues;
      if (checked) {
        const option = question.options?.find(opt => opt.value === optionValue);
        if (option?.isOther && otherInputs[optionValue]) {
          // Wenn "Anderes" Option gewählt wird und bereits Text vorhanden ist, kombiniere
          const combinedValue = `${optionValue}:${otherInputs[optionValue]}`;
          newValues = [...selectedValues, combinedValue];
        } else {
          newValues = [...selectedValues, optionValue];
        }
      } else {
        newValues = selectedValues.filter((val: string) => val !== optionValue && !val.startsWith(`${optionValue}:`));
      }
      
      // Bewahre die Artikel-Struktur, wenn sie bereits existiert
      if (typeof currentAnswer === 'object' && currentAnswer?.articles) {
        handleAnswerChange({
          value: newValues,
          articles: currentAnswer.articles
        });
      } else {
        handleAnswerChange(newValues);
      }
    };

    const handleOtherInputChange = (optionValue: string, inputValue: string) => {
      setOtherInputs(prev => ({
        ...prev,
        [optionValue]: inputValue
      }));
      
      // Ersetze die bestehende Option mit der kombinierten Version (entferne sowohl plain als auch combined)
      const newValues = selectedValues.filter((val: string) => val !== optionValue && !val.startsWith(`${optionValue}:`));
      const combinedValue = `${optionValue}:${inputValue}`;
      newValues.push(combinedValue);
      
      // Bewahre die Artikel-Struktur, wenn sie bereits existiert
      if (typeof currentAnswer === 'object' && currentAnswer?.articles) {
        handleAnswerChange({
          value: newValues,
          articles: currentAnswer.articles
        });
      } else {
        handleAnswerChange(newValues);
      }
    };

    return (
      <div className="space-y-3">
        {question.options?.map((option) => {
          const isSelected = selectedValues.includes(option.value) || selectedValues.some((val: string) => val.startsWith(`${option.value}:`));
          const otherInputValue = otherInputs[option.value] || '';
          
          return (
            <div key={option.value}>
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleOptionChange(option.value, e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={readOnly}
                  />
                  <span className="text-gray-900">{getOptionLabel(option, language)}</span>
                </label>
                {/* Artikel-Auswahl Button für diese Option */}
                {question.requiresArticleSelection && (
                  <button
                    type="button"
                    onClick={() => setShowArticleModal(`${question.id}_${option.value}`)}
                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faBox} className="mr-1" />
                    {getSelectedArticlesForOption(`${question.id}_${option.value}`).length > 0
                      ? `${getSelectedArticlesForOption(`${question.id}_${option.value}`).length} ${language === 'en' ? (getSelectedArticlesForOption(`${question.id}_${option.value}`).length === 1 ? 'Article' : 'Articles') : 'Artikel'}`
                      : (language === 'en' ? 'Select articles' : 'Artikel wählen')
                    }
                  </button>
                )}
              </div>
              {/* Textfeld für "Anderes:" Option */}
              {option.isOther && isSelected && (
                <div className="ml-7 mt-2">
                  <input
                    type="text"
                    placeholder={language === 'en' ? 'Please specify...' : 'Bitte spezifizieren...'}
                    value={otherInputValue}
                    onChange={(e) => handleOtherInputChange(option.value, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // **RENDER FILE UPLOAD - COMPLETELY UPDATED**
  const renderFileUpload = () => {
    if (readOnly) {
      return (
        <div className="space-y-4">
          {existingFiles.length > 0 ? (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Hochgeladene Dateien ({existingFiles.length})</h4>
              <div className="space-y-2">
                {existingFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{getFileName(file)}</p>
                        {typeof file === 'object' && file.size && (
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{language === 'en' ? 'No files uploaded' : 'Keine Dateien hochgeladen'}</p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById(`file-input-${question.id}`)?.click()}
        >
          <div className="space-y-2">
            <div className="text-gray-600">
              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-sm font-medium text-gray-900">
              {language === 'en' ? 'Drag & drop or click to select' : 'Drag & Drop oder klicken zum Auswählen'}
            </div>
            <div className="text-xs text-gray-500">
              <p>{language === 'en' ? 'Allowed formats:' : 'Erlaubte Formate:'}</p>
              <p className="mt-1">
                {(() => {
                  const types = (question.validation?.fileTypes && question.validation.fileTypes.length > 0) 
                    ? question.validation.fileTypes 
                    : ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
                  return types.map(type => `.${type}`).join(', ');
                })()}
              </p>
            </div>
          </div>
        </div>
        
        <input
          id={`file-input-${question.id}`}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          accept={(() => {
            const types = (question.validation?.fileTypes && question.validation.fileTypes.length > 0) 
              ? question.validation.fileTypes 
              : ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
            return types.map(type => `.${type}`).join(',');
          })()}
        />

        {/* **NEUE DATEIEN ZUM HOCHLADEN** */}
        {files.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Wird hochgeladen... ({files.length})</h4>
            <div className="space-y-2">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {isSaving ? (
                        <svg className="animate-spin w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isSaving ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {isSaving ? 'Uploading...' : 'Bereit'}
                    </span>
                    {!isSaving && (
                      <button
                        onClick={() => removeQueuedFile(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Aus Warteschlange entfernen"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* **HOCHGELADENE DATEIEN** */}
        {existingFiles.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Hochgeladene Dateien ({existingFiles.length})</h4>
            <div className="space-y-2">
              {existingFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{getFileName(file)}</p>
                      {typeof file === 'object' && file.size && (
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeExistingFile(index)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                    title={language === 'en' ? 'Delete file' : 'Datei löschen'}
                  >
                    {language === 'en' ? 'Remove' : 'Entfernen'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* **REQUIRED FIELD WARNING** */}
        {(question.required || question.validation?.required) && isRequiredAndEmpty() && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-medium">
              {language === 'en' 
                ? '📎 Required field: Please upload at least one file' 
                : '📎 Pflichtfeld: Bitte laden Sie mindestens eine Datei hoch'
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderSignature = () => {
    if (readOnly) {
      return (
        <div className="space-y-4">
          {hasSignature && currentAnswer ? (
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <img src={currentAnswer} alt="Unterschrift" className="max-w-full h-auto" />
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{language === 'en' ? 'No signature available' : 'Keine Unterschrift vorhanden'}</p>
          )}
        </div>
      );
    }

    return (
      <SignatureErrorBoundary>
        <div className="space-y-4">
          {!hasSignature ? (
            <div className="space-y-4">
              {/* **FIXED CONTAINER WITH PROPER DIMENSIONS** */}
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                {/* **SIGNATURE CANVAS CONTAINER - FIXED SIZING** */}
                <div className="relative bg-white rounded" style={{ width: '100%', height: '200px' }}>
                  <SignatureCanvas
                    key={signatureKey}
                    ref={signatureRef}
                    canvasProps={{
                      width: 800,
                      height: 200,
                      className: 'signature-canvas cursor-crosshair rounded',
                      style: {
                        width: '100%',
                        height: '200px',
                        display: 'block',
                        touchAction: 'none'
                      }
                    }}
                    backgroundColor="white"
                    penColor="black"
                    dotSize={1}
                    minWidth={1}
                    maxWidth={2.5}
                    throttle={0}
                    velocityFilterWeight={0.7}
                    // **FIX OFFSET ISSUES - THESE ARE THE KEY PROPERTIES**
                    clearOnResize={false}
                  />
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSignatureConfirm}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {language === 'en' ? 'Confirm signature' : 'Unterschrift bestätigen'}
                </button>
                <button
                  type="button"
                  onClick={handleSignatureClear}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {language === 'en' ? 'Clear' : 'Löschen'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-green-800">{language === 'en' ? 'Signature captured' : 'Unterschrift erfasst'}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleSignatureClear}
                className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 focus:outline-none focus:ring-1 focus:ring-red-400"
              >
                {language === 'en' ? '🗑️ Delete' : '🗑️ Löschen'}
              </button>
            </div>
          )}
          {(question.required || question.validation?.required) && isRequiredAndEmpty() && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">
                {language === 'en' 
                  ? '✍️ Required field: Please sign' 
                  : '✍️ Pflichtfeld: Bitte setzen Sie Ihre Unterschrift'
                }
              </p>
            </div>
          )}
        </div>
      </SignatureErrorBoundary>
    );
  };

  // **INFO TEXT RENDERER**
  const renderInfoText = () => {
    // Priorisiere das label Feld für info_text Fragen
    const labelText = question.type === 'info_text' && typeof question.label === 'object' && question.label 
      ? question.label[language as 'de' | 'en'] || question.label['de']
      : '';
    const infoText = labelText || question.infoText?.[language as 'de' | 'en'] || question.infoText?.de || '';
    
    if (!infoText) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500 text-sm italic">{language === 'en' ? 'No information text available' : 'Kein Informationstext verfügbar'}</p>
        </div>
      );
    }

    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        {renderTextContent(infoText, "text-gray-700 leading-relaxed")}
      </div>
    );
  };

  // **FILE DOWNLOAD HANDLER**
  const handleFileDownload = async (file: any) => {
    try {
      console.log('Download file object (main):', file);
      console.log('fileName:', file.fileName);
      console.log('originalName:', file.originalName);
      
      const downloadUrl = `/api/questionnaires/admin-files/${file.fileName}`;
      console.log('Download URL:', downloadUrl);
      
      // **TESTE ZUERST MIT FETCH OB DER ENDPOINT ERREICHBAR IST**
      console.log('🔍 Testing download endpoint with fetch...');
      const response = await fetch(downloadUrl);
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Download failed:', response.status, errorText);
        alert(`Download fehlgeschlagen: ${response.status} - ${errorText}`);
        return;
      }
      
      // **WENN FETCH FUNKTIONIERT, MACHE NORMALEN DOWNLOAD**
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalName || file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('✅ Download successful');
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(`Download fehlgeschlagen: ${error}`);
    }
  };

  // **ADMIN FILE RENDERER**
  const renderAdminFile = () => {
    const adminFiles = question.adminFiles || [];
    
    if (adminFiles.length === 0) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500 text-sm italic">{language === 'en' ? 'No files available' : 'Keine Dateien verfügbar'}</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-3">
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-start space-x-2 mb-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-800 mb-1">
                {language === 'en' ? 'Available files for download' : 'Verfügbare Dateien zum Download'}
              </h4>
              <p className="text-xs text-green-700">
                {language === 'en' ? 'Click on a file to download it.' : 'Klicken Sie auf eine Datei, um sie herunterzuladen.'}
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            {adminFiles.map((file: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-300 hover:bg-green-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                    <p className="text-xs text-gray-500">
                      {file.size ? `${(file.size / 1024).toFixed(1)} KB` : (language === 'en' ? 'Unknown size' : 'Unbekannte Größe')} • 
                      {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE') : (language === 'en' ? 'Unknown date' : 'Unbekanntes Datum')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleFileDownload(file)}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // **ARTIKEL AUSWAHL RENDERER**
  const renderArticleSelection = () => {
    if (!question.requiresArticleSelection) {
      return null;
    }

    // Filtere Artikel basierend auf den Option-spezifischen articleIds (falls vorhanden)
    let relevantArticleIds: string[] = [];
    
    // Wenn die aktuelle Antwort eine Option mit articleIds ist
    if (question.options && question.options.length > 0) {
      const selectedOption = question.options.find(opt => 
        opt.value === currentAnswer || 
        (Array.isArray(currentAnswer) && currentAnswer.includes(opt.value))
      );
      if (selectedOption && selectedOption.articleIds && selectedOption.articleIds.length > 0) {
        relevantArticleIds = selectedOption.articleIds;
      }
    }

    // Filtere die verfügbaren Artikel
    const articlesToShow = relevantArticleIds.length > 0 
      ? availableArticles.filter(article => relevantArticleIds.includes(article._id))
      : availableArticles;

    return (
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">
            {language === 'en' 
              ? `Select articles${question.allowMultipleArticles ? ' (multiple selection possible)' : ''}`
              : `Artikel auswählen${question.allowMultipleArticles ? ' (Mehrfachauswahl möglich)' : ''}`
            }
          </h4>
          <span className="text-xs text-gray-500">
            {selectedArticleAnswer.length} {language === 'en' ? 'of' : 'von'} {articlesToShow.length} {language === 'en' ? 'available' : 'verfügbar'}
          </span>
        </div>
        
        {/* **POPUP BUTTON** */}
        <button
          onClick={() => setShowArticleSelector(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
          disabled={readOnly}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7m16 0L12 11 4 7" />
          </svg>
          <span>
            {articlesToShow.length === 0 
              ? (language === 'en' ? 'No articles available' : 'Keine Artikel verfügbar')
              : selectedArticleAnswer.length > 0 
                ? `${selectedArticleAnswer.length} ${language === 'en' ? (selectedArticleAnswer.length === 1 ? 'Article selected - Click to change' : 'Articles selected - Click to change') : 'Artikel ausgewählt - Klicken zum Ändern'}`
                : (language === 'en' ? 'Select articles' : 'Artikel auswählen')
            }
          </span>
        </button>

        {/* **AUSGEWÄHLTE ARTIKEL ANZEIGE** */}
        {selectedArticleAnswer.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-gray-600 font-medium">Ausgewählte Artikel:</p>
            <div className="space-y-1">
              {selectedArticleAnswer.map(articleId => {
                const article = articlesToShow.find(a => a._id === articleId);
                if (!article) return null;
                
                return (
                  <div key={articleId} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <div>
                        <span className="text-sm font-medium text-gray-900">{article.name}</span>
                        <p className="text-xs text-gray-600">{article.articleNumber}</p>
                      </div>
                    </div>
                    <span className="text-xs text-blue-600 font-medium">✓</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Validation für erforderliche Artikel-Auswahl */}
        {question.required && selectedArticleAnswer.length === 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 font-medium">
              {language === 'en' 
                ? '📦 Required field: Please select at least one article' 
                : '📦 Pflichtfeld: Bitte wählen Sie mindestens einen Artikel aus'
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderQuestion = () => {
    switch (question.type) {
      case 'text':
        return renderTextInput();
      case 'textarea':
        return renderTextarea();
      case 'select':
        return renderSelect();
      case 'radio':
        return renderRadio();
      case 'yesno':
        return renderYesNo();
      case 'checkbox':
        return renderCheckbox();
      case 'section':
        return renderSection();
      case 'date':
        return renderDate();
      case 'multiselect':
        return renderMultiselect();
      case 'file':
        return renderFileUpload();
      case 'signature':
        return renderSignature();
      case 'info_text':
        return renderInfoText();
      case 'admin_file':
        return renderAdminFile();
      default:
        return <div className="text-gray-500">{language === 'en' ? `Unknown question type: ${question.type}` : `Unbekannter Fragetyp: ${question.type}`}</div>;
    }
  };

  // **MAIN RENDER**
  // **SECTION (📚 Überschrift) - sammelt alle nachfolgenden Fragen bis zur nächsten Section**
  if (question.type === 'section') {
    const followingElements = getFollowingInfoQuestions();
    
    return (
      <div className="space-y-6">
        {/* Section Überschrift */}
        <div className="border-b-2 border-blue-500 pb-3">
          <h2 className="text-2xl font-bold text-gray-900">
            {getLabel(question.label || question.title, language)}
          </h2>
        </div>

        {/* Alle nachfolgenden Fragen unter dieser Section */}
        {followingElements.map((elem, idx) => {
          // **WICHTIG: Erstelle einen neuen onSave-Callback für jede Unterfrage
          // der die RICHTIGE Question-ID verwendet, nicht die Section-ID**
          const wrappedOnSave = async (questionId: string, answer: any, files: File[]) => {
            // Jetzt können Unterfragen ihre eigene ID übergeben!
            console.log(`✅ Section child question ${elem.id} calling onSave with correct questionId:`, questionId);
            return onSave(questionId, answer, files);
          };
          
          return (
            <div key={`section-question-${elem.id}-${idx}`} className="space-y-4">
              {/* Render jede Frage mit QuestionRenderer rekursiv */}
              <QuestionRenderer
                question={elem}
                answer={allAnswers?.get(elem.id)}
                onSave={wrappedOnSave}
                language={language}
                readOnly={readOnly}
                supplierName={supplierName}
                supplierId={supplierId}
                allQuestions={allQuestions}
                currentIndex={-1}
                allAnswers={allAnswers}
                availableArticles={availableArticles}
                selectedArticleAnswer={selectedArticleAnswer}
                onArticleSelectionChange={onArticleSelectionChange}
              />
            </div>
          );
        })}
      </div>
    );
  }
  
  // **RENDER standalone Info-Text (sollte nicht mehr vorkommen)**
  if (question.type === 'info_text') {
    return (
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        {(() => {
          const labelText = typeof question.label === 'object' && question.label 
            ? question.label[language as 'de' | 'en'] || question.label['de']
            : '';
          const text = labelText || question.infoText?.[language as 'de' | 'en'] || question.infoText?.de || '';
          return renderTextContent(text, "text-gray-700 leading-relaxed");
        })()}
      </div>
    );
  }

  // **RENDER standalone Admin-Datei (sollte nicht mehr vorkommen)**
  if (question.type === 'admin_file') {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-gray-900">
            {getLabel(question.label || question.title, language)}
          </h3>
        </div>
        {renderAdminFile()}
      </div>
    );
  }

  // **Get following info questions to render with this question**
  const followingInfoQuestions = getFollowingInfoQuestions();

  return (
    <div className="space-y-4">
      {/* **QUESTION LABEL** */}
      <div className="space-y-2">
        <h3 className="text-lg font-medium text-gray-900">
          {getLabel(question.label || question.title, language)}
          {(question.required || question.validation?.required) && (
            <span className="text-red-500 ml-1 font-bold" title={language === 'en' ? 'Required field' : 'Pflichtfeld'}>*</span>
          )}
        </h3>
        {(question.required || question.validation?.required) && (
          <p className="text-xs text-red-500 font-medium">
            {language === 'en' ? '📋 Required field' : '📋 Pflichtfeld'}
          </p>
        )}
      </div>

      {/* **QUESTION INPUT** */}
      {renderQuestion()}

      {/* **FOLLOWING INFO-TEXT AND ADMIN-FILES** */}
      {followingInfoQuestions.map((infoQuestion, index) => (
        <div key={infoQuestion.id} className="mt-4">
          {infoQuestion.type === 'info_text' && (
            <div className="space-y-2">
              <div className="p-4 bg-gray-50 rounded-lg">
                {(() => {
                  const labelText = typeof infoQuestion.label === 'object' && infoQuestion.label 
                    ? infoQuestion.label[language as 'de' | 'en'] || infoQuestion.label['de']
                    : '';
                  const text = labelText || infoQuestion.infoText?.[language as 'de' | 'en'] || infoQuestion.infoText?.de || '';
                  return renderTextContent(text, "text-gray-700 leading-relaxed");
                })()}
              </div>
            </div>
          )}
          
          {infoQuestion.type === 'admin_file' && (
            <div className="space-y-2">
              <h4 className="text-base font-medium text-gray-800">
                {getLabel(infoQuestion.label || infoQuestion.title, language)}
              </h4>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                {(() => {
                  const adminFiles = infoQuestion.adminFiles || [];
                  
                  if (adminFiles.length === 0) {
                    return (
                      <p className="text-gray-500 text-sm italic">{language === 'en' ? 'No files available' : 'Keine Dateien verfügbar'}</p>
                    );
                  }

                  const handleFileDownload = async (file: any) => {
                    try {
                      console.log('Download file object:', file);
                      console.log('fileName:', file.fileName);
                      console.log('originalName:', file.originalName);
                      
                      const downloadUrl = `/api/questionnaires/admin-files/${file.fileName}`;
                      console.log('Download URL:', downloadUrl);
                      
                      // **TESTE ZUERST MIT FETCH OB DER ENDPOINT ERREICHBAR IST**
                      console.log('🔍 Testing download endpoint with fetch...');
                      const response = await fetch(downloadUrl);
                      console.log('📡 Response status:', response.status);
                      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
                      
                      if (!response.ok) {
                        const errorText = await response.text();
                        console.error('❌ Download failed:', response.status, errorText);
                        alert(`Download fehlgeschlagen: ${response.status} - ${errorText}`);
                        return;
                      }
                      
                      // **WENN FETCH FUNKTIONIERT, MACHE NORMALEN DOWNLOAD**
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = file.originalName || file.fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      console.log('✅ Download successful');
                    } catch (error) {
                      console.error('💥 Error downloading file:', error);
                      alert(`Download Fehler: ${error}`);
                    }
                  };

                  return (
                    <>
                      <div className="flex items-start space-x-2 mb-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-green-800 mb-1">
                            {language === 'en' ? 'Available files for download' : 'Verfügbare Dateien zum Download'}
                          </h5>
                          <p className="text-xs text-green-700">
                            {language === 'en' ? 'Click on a file to download it.' : 'Klicken Sie auf eine Datei, um sie herunterzuladen.'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {adminFiles.map((file: any, fileIndex: number) => (
                          <div key={fileIndex} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-300 hover:bg-green-50 transition-colors">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                                <p className="text-xs text-gray-500">
                                  {file.size ? `${(file.size / 1024).toFixed(1)} KB` : (language === 'en' ? 'Unknown size' : 'Unbekannte Größe')} • 
                                  {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE') : (language === 'en' ? 'Unknown date' : 'Unbekanntes Datum')}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleFileDownload(file)}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                            >
                              Download
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* **VALIDATION MESSAGE** */}
      {validationMessage && (
        <div className={`text-sm ${isValidationError ? 'text-red-600' : 'text-green-600'}`}>
          {validationMessage}
        </div>
      )}

      {/* **SAVE INDICATOR** */}
      {isSaving && (
        <div className="text-sm text-blue-600 flex items-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {question.type === 'file' 
            ? (language === 'en' ? 'Uploading files...' : 'Dateien werden hochgeladen...') 
            : (language === 'en' ? 'Saving...' : 'Speichert...')
          }
        </div>
      )}

      {!isSaving && !isValidationError && !validationMessage && isDirty === false && (
        <div className="text-sm text-green-600">
          {question.type === 'file' 
            ? (language === 'en' ? '✓ Files uploaded' : '✓ Dateien hochgeladen') 
            : (language === 'en' ? '✓ Automatically saved' : '✓ Automatisch gespeichert')
          }
        </div>
      )}
      
      {/* **ARTIKEL SELECTION MODAL** */}
      {question.requiresArticleSelection && showArticleSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => closeArticleSelector()}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {language === 'en' ? 'Select articles for questionnaire' : 'Artikel für Fragebogen auswählen'}
              </h3>
              <button
                onClick={() => closeArticleSelector()}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {/* Search Field */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder={language === 'en' ? 'Search articles...' : 'Artikel suchen...'}
                  value={articleSearchQuery}
                  onChange={(e) => setArticleSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  {language === 'en' 
                    ? (question.allowMultipleArticles ? 'Multiple selection possible' : 'Only one selection possible')
                    : (question.allowMultipleArticles ? 'Mehrfachauswahl möglich' : 'Nur eine Auswahl möglich')
                  }
                </p>
                <span className="text-sm font-medium text-gray-900">
                  {selectedArticleAnswer.length} {language === 'en' ? (selectedArticleAnswer.length === 1 ? 'Article selected' : 'Articles selected') : 'Artikel ausgewählt'}
                </span>
              </div>
              
              {/* Artikel Liste */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availableArticles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7m16 0L12 11 4 7" />
                    </svg>
                    <p className="text-sm font-medium">{language === 'en' ? 'No articles available' : 'Keine Artikel verfügbar'}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {language === 'en' 
                        ? 'Please contact the administrator if you need articles to select.'
                        : 'Bitte wenden Sie sich an den Administrator, wenn Sie Artikel zur Auswahl benötigen.'
                      }
                    </p>
                  </div>
                ) : (
                  availableArticles
                    .filter(article => {
                      if (!articleSearchQuery) return true;
                      const query = articleSearchQuery.toLowerCase();
                      return (
                        article.name?.toLowerCase().includes(query) ||
                        article.articleNumber?.toLowerCase().includes(query) ||
                        article.description?.toLowerCase().includes(query)
                      );
                    })
                    .map((article) => {
                    const isSelected = selectedArticleAnswer.includes(article._id);
                    const getSupplierName = (supplierId: string | { name: string }) => {
                      if (typeof supplierId === 'string') {
                        return supplierId;
                      }
                      return supplierId.name || (language === 'en' ? 'Unknown Supplier' : 'Unbekannter Lieferant');
                    };

                    const handleArticleToggle = (articleId: string, checked: boolean) => {
                      let newSelection: string[];
                      
                      if (question.allowMultipleArticles) {
                        // Mehrfachauswahl erlaubt
                        if (checked) {
                          newSelection = [...selectedArticleAnswer, articleId];
                        } else {
                          newSelection = selectedArticleAnswer.filter(id => id !== articleId);
                        }
                      } else {
                        // Nur eine Auswahl erlaubt
                        if (checked) {
                          newSelection = [articleId];
                        } else {
                          newSelection = [];
                        }
                      }
                      
                      if (onArticleSelectionChange) {
                        onArticleSelectionChange(newSelection);
                      }
                    };
                    
                    return (
                      <div 
                        key={article._id} 
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => handleArticleToggle(article._id, !isSelected)}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type={question.allowMultipleArticles ? "checkbox" : "radio"}
                            name={question.allowMultipleArticles ? undefined : `article-${question.id}`}
                            checked={isSelected}
                            onChange={(e) => handleArticleToggle(article._id, e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            disabled={readOnly}
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{article.name}</span>
                              {isSelected && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Ausgewählt
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">
                              {article.articleNumber} • {getSupplierName(article.supplierId)}
                            </p>
                            {article.description && (
                              <p className="text-sm text-gray-500 mt-1">
                                {article.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-600">
                {selectedArticleAnswer.length} von {availableArticles.length} Artikel ausgewählt
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => closeArticleSelector()}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  {language === 'en' ? 'Cancel' : 'Abbrechen'}
                </button>
                <button
                  onClick={() => closeArticleSelector()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {language === 'en' ? 'Select' : 'Auswählen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* **OPTION-SPEZIFISCHES ARTIKEL SELECTION MODAL** */}
      {question.requiresArticleSelection && showArticleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => closeArticleModal()}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {language === 'en' ? 'Select articles' : 'Artikel auswählen'}
                  {(() => {
                    const optionKeyParts = showArticleModal.split('_');
                    const choice = optionKeyParts[optionKeyParts.length - 1];
                    if (choice === 'text') {
                      return language === 'en' ? ' for answer' : ' für Antwort';
                    } else if (choice === 'ja' || choice === 'nein') {
                      return ` ${language === 'en' ? 'for' : 'für'} "${choice === 'ja' ? (language === 'en' ? 'Yes' : 'Ja') : (language === 'en' ? 'No' : 'Nein')}"`;
                    } else {
                      // Für Optionen von Radio/Checkbox
                      const option = question.options?.find(opt => opt.value === choice);
                      if (option) {
                        const label = getOptionLabel(option, language);
                        return ` ${language === 'en' ? 'for' : 'für'} "${label}"`;
                      }
                    }
                    return '';
                  })()}
                </h3>
                {supplierName && (
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'en' ? 'Supplier:' : 'Lieferant:'} {supplierName}
                  </div>
                )}
              </div>
              <button
                onClick={() => closeArticleModal()}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {/* Search Field */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder={language === 'en' ? 'Search articles...' : 'Artikel suchen...'}
                  value={articleModalSearchQuery}
                  onChange={(e) => setArticleModalSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600">
                    {language === 'en' 
                      ? (question.allowMultipleArticles ? 'Multiple selection possible' : 'Only one selection possible')
                      : (question.allowMultipleArticles ? 'Mehrfachauswahl möglich' : 'Nur eine Auswahl möglich')
                    }
                  </p>
                  {question.allowMultipleArticles && availableArticles.length > 0 && (
                    <div className="flex items-center space-x-3 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          const currentSelection = getSelectedArticlesForOption(showArticleModal);
                          const availableNotInOther = availableArticles.filter(article => 
                            !isArticleSelectedInOtherOption(article._id, showArticleModal)
                          );
                          const allAvailableIds = availableNotInOther.map(a => a._id);
                          handleArticleSelectionForOption(showArticleModal, allAvailableIds);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {language === 'en' ? 'Select all' : 'Alle auswählen'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleArticleSelectionForOption(showArticleModal, []);
                        }}
                        className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                      >
                        {language === 'en' ? 'Deselect all' : 'Alle abwählen'}
                      </button>
                    </div>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {getSelectedArticlesForOption(showArticleModal).length} {language === 'en' ? (getSelectedArticlesForOption(showArticleModal).length === 1 ? 'Article selected' : 'Articles selected') : 'Artikel ausgewählt'}
                </span>
              </div>
              
              {/* Artikel Liste */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availableArticles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7m16 0L12 11 4 7" />
                    </svg>
                    <p className="text-sm font-medium">{language === 'en' ? 'No articles available' : 'Keine Artikel verfügbar'}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {language === 'en' 
                        ? 'Please contact the administrator if you need articles to select.'
                        : 'Bitte wenden Sie sich an den Administrator, wenn Sie Artikel zur Auswahl benötigen.'
                      }
                    </p>
                  </div>
                ) : (
                  availableArticles
                    .filter(article => {
                      if (!articleModalSearchQuery) return true;
                      const query = articleModalSearchQuery.toLowerCase();
                      return (
                        article.name?.toLowerCase().includes(query) ||
                        article.articleNumber?.toLowerCase().includes(query) ||
                        article.description?.toLowerCase().includes(query)
                      );
                    })
                    .map((article) => {
                    const currentlySelected = getSelectedArticlesForOption(showArticleModal);
                    const isSelected = currentlySelected.includes(article._id);
                    const isSelectedInOtherOption = isArticleSelectedInOtherOption(article._id, showArticleModal);
                    
                    // Ein Artikel ist nur dann disabled, wenn er in der anderen Option ausgewählt ist
                    // UND nicht in der aktuellen Option ausgewählt ist
                    const isDisabled = isSelectedInOtherOption && !isSelected;
                    
                    const handleArticleToggle = (articleId: string, checked: boolean) => {
                      // Nicht erlauben, wenn Artikel in anderer Option bereits ausgewählt und hier nicht ausgewählt
                      if (isDisabled) return;
                      
                      let newSelection: string[];
                      
                      if (question.allowMultipleArticles) {
                        // Mehrfachauswahl erlaubt
                        if (checked) {
                          newSelection = [...currentlySelected, articleId];
                        } else {
                          newSelection = currentlySelected.filter(id => id !== articleId);
                        }
                      } else {
                        // Nur eine Auswahl erlaubt
                        if (checked) {
                          newSelection = [articleId];
                        } else {
                          newSelection = [];
                        }
                      }
                      
                      handleArticleSelectionForOption(showArticleModal, newSelection);
                    };
                    
                    return (
                      <div 
                        key={article._id} 
                        className={`p-3 border rounded-lg transition-colors ${
                          isDisabled
                            ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                            : isSelected 
                              ? 'bg-blue-50 border-blue-300 cursor-pointer' 
                              : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
                        }`}
                        onClick={() => !isDisabled && handleArticleToggle(article._id, !isSelected)}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type={question.allowMultipleArticles ? "checkbox" : "radio"}
                            name={question.allowMultipleArticles ? undefined : `article-${question.id}-${showArticleModal}`}
                            checked={isSelected}
                            onChange={(e) => !isDisabled && handleArticleToggle(article._id, e.target.checked)}
                            className={`w-4 h-4 border-gray-300 rounded focus:ring-blue-500 ${
                              isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600'
                            }`}
                            disabled={readOnly || isDisabled}
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className={`font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                                {article.name}
                              </span>
                              {isSelected && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Ausgewählt
                                </span>
                              )}
                              {isDisabled && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  Bei {showArticleModal.split('_')[1] === 'ja' ? 'Nein' : 'Ja'} ausgewählt
                                </span>
                              )}
                            </div>
                            <div className={`text-sm space-y-1 ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                              <div>{article.articleNumber}</div>
                              {article.description && <div className="text-xs">{article.description}</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => closeArticleModal()}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  {language === 'en' ? 'Cancel' : 'Abbrechen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Bei Mehrfachauswahl: Modal explizit schließen
                    if (question.allowMultipleArticles) {
                      const currentSelection = getSelectedArticlesForOption(showArticleModal || '');
                      handleArticleSelectionForOption(showArticleModal, currentSelection, true);
                    } else {
                      closeArticleModal();
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  {language === 'en' 
                    ? `Confirm selection (${getSelectedArticlesForOption(showArticleModal || '').length})`
                    : `Auswahl bestätigen (${getSelectedArticlesForOption(showArticleModal || '').length})`
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionRenderer;